import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api, ApiError } from '../lib/api';
import type { AdminProfile, ContentBundle, Progress, VendorProfile } from '../lib/types';

interface SessionValue {
  vendor: VendorProfile | null;
  /**
   * A staff session. Mutually exclusive with `vendor` — one session cookie, one
   * subject. The app renders either the vendor portal or the admin console, never both.
   */
  admin: AdminProfile | null;
  content: ContentBundle | null;
  progress: Progress;
  loading: boolean;
  /** Resolves to the role that was signed in, so the caller knows where to send them. */
  signIn: (email: string, password: string) => Promise<'vendor' | 'admin'>;
  signOut: () => Promise<void>;
  toggle: (bucket: keyof Progress, key: string, next: boolean) => void;
  clear: (bucket: keyof Progress) => Promise<void>;
}

const EMPTY: Progress = { done: {}, ticks: {}, grade: {} };
const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [vendor, setVendor] = useState<VendorProfile | null>(null);
  const [admin, setAdmin] = useState<AdminProfile | null>(null);
  const [content, setContent] = useState<ContentBundle | null>(null);
  const [progress, setProgress] = useState<Progress>(EMPTY);
  const [loading, setLoading] = useState(true);

  /**
   * The curriculum and progress are fetched once per session, not per navigation — the
   * whole corpus is ~37 lessons of prose and the prototype's search, counters and
   * "next up" logic all assume the full set is in hand.
   */
  const hydrate = useCallback(async () => {
    const [bundle, prog] = await Promise.all([api.content(), api.progress()]);
    setContent(bundle);
    setProgress(prog);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { vendor: v, admin: a } = await api.session();
        if (cancelled) return;
        setVendor(v);
        setAdmin(a);
        // Only fetch the curriculum once we know there is somebody to show it to.
        // Admins don't need it: the console loads its own raw-document bundle.
        if (v) await hydrate();
      } catch (err) {
        // Failing to confirm a session is not a fault condition: whatever the cause —
        // a 401 (not signed in), a torn-down effect, a dropped connection, or a fetch
        // the browser aborted because the page navigated away (which surfaces as a bare
        // TypeError, not an AbortError) — the correct response is the same, and the
        // `finally` below already delivers it by showing the sign-in screen.
        //
        // So this is a warning, not an error. Logging it at error level made a normal
        // navigation look like a crash.
        if (!cancelled && !(err instanceof ApiError && err.status === 401)) {
          console.warn('[vu] could not confirm session; showing sign-in', err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hydrate]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const { role, vendor: v, admin: a } = await api.login(email, password);

      if (role === 'admin') {
        setAdmin(a);
        // Staff never load the vendor curriculum; the console fetches its own bundle.
        return 'admin' as const;
      }

      setVendor(v);
      await hydrate();
      return 'vendor' as const;
    },
    [hydrate],
  );

  const signOut = useCallback(async () => {
    await api.logout();
    setVendor(null);
    setAdmin(null);
    setContent(null);
    setProgress(EMPTY);
  }, []);

  /**
   * Optimistic: ticking a checkbox must feel instant, and a failed write is recoverable
   * (the next load shows the server's truth). We roll back on error so the UI never
   * claims a save that did not happen.
   */
  const toggle = useCallback((bucket: keyof Progress, key: string, next: boolean) => {
    setProgress((prev) => {
      const bucketState = { ...prev[bucket] };
      if (next) bucketState[key] = true;
      else delete bucketState[key];
      return { ...prev, [bucket]: bucketState };
    });

    api.setProgress(bucket, key, next).catch((err) => {
      console.error('Could not save progress', err);
      setProgress((prev) => {
        const bucketState = { ...prev[bucket] };
        if (next) delete bucketState[key];
        else bucketState[key] = true;
        return { ...prev, [bucket]: bucketState };
      });
    });
  }, []);

  const clear = useCallback(async (bucket: keyof Progress) => {
    setProgress(await api.clearProgress(bucket));
  }, []);

  const value = useMemo(
    () => ({ vendor, admin, content, progress, loading, signIn, signOut, toggle, clear }),
    [vendor, admin, content, progress, loading, signIn, signOut, toggle, clear],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside a SessionProvider');
  return ctx;
}

/** Content is only ever read inside authenticated routes, where it is guaranteed loaded. */
export function useContent(): ContentBundle {
  const { content } = useSession();
  if (!content) throw new Error('Content accessed before it finished loading');
  return content;
}
